import React, { useState, useEffect } from 'react'
import { useDrop } from 'react-dnd'
import { useSelector, useDispatch } from 'react-redux'
import { setFx, resetFx, setBars, setKey, setMode, setBpm } from './editorSlice'
import { MAX_BARS, FX_CATEGORIES } from './editorConstants'
import { getPitchJSX, getProgression, getProgressionJSX } from './editorUtils'
import { IconButton, Grow, Popper, Fade, ListItemButton, Collapse, ListSubheader} from '@mui/material'
import { makeStyles } from '@mui/styles'
import PitchSelect from './PitchSelect'
import StyledTypography from '../../components/StyledTypography'
import StyledSelect, {StyledMenu, StyledMenuItem} from '../../components/StyledSelect'
import StyledPopup from '../../components/StyledPopup'
import StyledSlider from '../../components/StyledSlider'
import InputPopup from '../../components/InputPopup'
import StyledDivider from '../../components/StyledDivider'
import {StyledListItem, StyledListItemText} from '../../components/StyledList'
import './LoopAttributes.css'
import { ReactComponent as FxSelectedIcon } from './icons/fx-selected.svg'
import { ReactComponent as FxUnselectedIcon } from './icons/fx-unselected.svg'
import {ReactComponent as GrooveLoopIcon} from './icons/groove-loop.svg'
import {ReactComponent as GrooveOneShotIcon} from './icons/groove-one-shot.svg'
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';


const useStyles = makeStyles(theme => ({
    popper: {
      zIndex: theme.zIndex.modal
    }
}));

export default function LoopAttributes(props){
    const {showProgression, setShowProgression, setProgressionDropItem, showWarning} = props
    const classes = useStyles();
    const clips = useSelector(state => state.editor.clips);
    const status = useSelector(state => state.editor.status);
    const fx = useSelector(state => state.editor.fx);
    const bars = useSelector(state => state.editor.bars);
    const key = useSelector(state => state.editor.key);
    const mode = useSelector(state => state.editor.mode);
    const bpm = useSelector(state => state.editor.bpm);
    const dispatch = useDispatch();
    const [fxAnchorEl, setFxAnchorEl] = useState(null);
    const [keyAnchorEl, setKeyAnchorEl] = useState(null);
    const [bpmAnchorEl, setBpmAnchorEl] = useState(null);
    const [bpmText, setBpmText] = useState(bpm.toString());
    const progression = getProgression(clips, bars);
    const [showDropIndicator, setShowDropIndicator] = useState(false);
    let hasFx = false;
    for (const category of FX_CATEGORIES)
        if (category.presets.includes(fx))
        {
            hasFx = true;
            break;
        }

    useEffect(() => {
        if(showProgression || status !== 'editing')
        {
            setKeyAnchorEl(null);
            setFxAnchorEl(null);
            setBpmAnchorEl(null);
        }
    }, [showProgression, status]); 

    const [{ isOver, canDrop, item }, drop] = useDrop(() => ({
        accept: 'group',
        drop: (item) => {setShowProgression(true); setProgressionDropItem(item)},
        canDrop: (item) => item.type === 'groove',
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop(),
            item: monitor.getItem()
        }),
    }));
    
    useEffect(() => {
        setBpmText(bpm.toString());
    }, [bpm, setBpmText]);

    function handleProgressionClick()
    {
        if (progression.length < 2)
        {
            setShowDropIndicator(true);
            setTimeout(() => {setShowDropIndicator(false)}, 1500);
            showWarning('Drop a groove to apply progressions to it');
        }
        else
        {
            setShowProgression(true);
            setProgressionDropItem(null);
        }
    }


    function FxCategory(category, index)
    {
        const [open, setOpen] = useState(false);
        useEffect (() => {
            setOpen(category.presets.includes(fx));
        }, [fxAnchorEl]);
        return (
        <div key={index} style={{minWidth: 350}}>
            <ListItemButton onClick={e => {e.stopPropagation(); setOpen(!open)}}>
                <StyledListItemText primary={category.name} />{open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {category.presets.map((preset, index) => <StyledMenuItem key={index} selected={fx === preset} autoFocus={fx === preset} onClick={() => {dispatch(setFx(preset)); setFxAnchorEl(null)}}>{preset}</StyledMenuItem>)}
            </Collapse>
            <StyledDivider style={{margin: '0 16px'}}/>
        </div>);
    }
    
    return (
        <div className={status !== 'editing' ? 'loop-attributes disabled' : 'loop-attributes'} onClick={() => {setFxAnchorEl(null); setKeyAnchorEl(null)}}>
            <IconButton disableRipple style={{marginRight: '7px'}} onClick={(e) => {e.stopPropagation(); setFxAnchorEl(Boolean(fxAnchorEl) ? null : e.currentTarget)}}>
                {hasFx ? <FxSelectedIcon /> : <FxUnselectedIcon />}
            </IconButton>
            <StyledMenu
                style={{transform: 'translateY(-13px)',  maxHeight: 500}}
                id="fx-menu"
                anchorEl={fxAnchorEl}
                open={Boolean(fxAnchorEl)}
                onClose={() => setFxAnchorEl(null)}
                transitionDuration={{ appear:150, enter: 150, exit: 0 }}
            >
                {hasFx && 
                <ListSubheader>
                    <StyledMenuItem onClick={() => {dispatch(resetFx()); setFxAnchorEl(null)}}>RESET</StyledMenuItem>
                    <StyledDivider />
                </ListSubheader>
                }
                {FX_CATEGORIES.map((category, index) => FxCategory(category, index))}
            </StyledMenu>
            <StyledTypography>Bars</StyledTypography>
            <StyledSelect style={{marginRight: '10px'}} handleChange={value => dispatch(setBars(value))} items={[...Array(MAX_BARS).keys()].map(i => i + 1)} value={bars}/>
            <StyledTypography>Key</StyledTypography>
            <StyledPopup style={{ marginRight: '10px', minWidth: '78px'}} value={<React.Fragment>{getPitchJSX(key)}{mode === 'major' ? '' : 'm'}</React.Fragment>} open={Boolean(keyAnchorEl)}
             handleClick={e => {e.stopPropagation();setKeyAnchorEl(Boolean(keyAnchorEl) ? null : e.currentTarget)}} />
            <Popper
                id={Boolean(keyAnchorEl) ? 'key-popper' : undefined}
                className={classes.popper}
                open={Boolean(keyAnchorEl)}
                anchorEl={keyAnchorEl}
                transition
                placement='top'
                modifiers={[{name: 'offset', options: {offset: [0, 26]}}]}
            >
                {({ TransitionProps }) => (
                <Grow {...TransitionProps} timeout={showProgression ? 0 : 350}>
                    <div className='key-popup' onClick={e => e.stopPropagation()} > 
                        <PitchSelect pitch={key} handlePitchChange={pitch => dispatch(setKey(pitch))}/>
                        <div className='key-divider' />
                        <div className='mode-select'>
                            <div className={mode === 'major' ? 'mode-slot selected' : 'mode-slot'} onClick={() => dispatch(setMode('major'))} >MAJOR</div>
                            <div className={mode === 'minor' ? 'mode-slot selected' : 'mode-slot'} onClick={() => dispatch(setMode('minor'))} >MINOR</div>
                        </div>
                    </div>
                </Grow>
                )}
            </Popper>
            <StyledTypography >BPM</StyledTypography>
            <InputPopup text={bpmText} setText={setBpmText} open={Boolean(bpmAnchorEl)} 
            handleClick={e => setBpmAnchorEl(Boolean(bpmAnchorEl) ? null : e.currentTarget)} handleBlur={() => {
                let value = bpmText === '' ? 40 : (bpmText.includes('.') ? parseFloat(parseFloat(bpmText).toFixed(1)): parseInt(bpmText));
                if (value < 40)
                    value = 40;
                else if (value > 200)
                    value = 200;
                setBpmText(value.toString());
                dispatch(setBpm(value));
            }}/>
            <StyledMenu
                style={{transform: 'translate(-35px, 16px)'}}
                id="bpm-menu"
                anchorEl={bpmAnchorEl}
                open={Boolean(bpmAnchorEl)}
                onClose={() => setBpmAnchorEl(null)}
            >
                <StyledListItem style={{width: '150px', height: '32px', paddingLeft: 7}}>
                    <StyledListItemText>40</StyledListItemText>
                    <StyledSlider value={bpm} min={40} max={200} onChange={(e, value) => dispatch(setBpm(value))}/>
                    <StyledListItemText>200</StyledListItemText>
                </StyledListItem>
            </StyledMenu>
            <StyledTypography >Progression</StyledTypography>
            <StyledPopup className='progression-select' drop={drop} arrowColor={canDrop || showDropIndicator ? '#F2F2F2' : '#5A5A5A'}style={{
                transform: (canDrop && isOver) || showDropIndicator ? 'translateX(-2px)' : 'none',
                minWidth: (canDrop && isOver) || showDropIndicator ? '412px' : '408px', 
                border: (canDrop && isOver) || showDropIndicator? '3px solid #8BDFFF' : '1px solid #EAEAEA'}} 
            display="flex" value={getProgressionJSX(progression, key)} open={false} handleClick={() => handleProgressionClick()} />
            <Fade in={showDropIndicator || canDrop} style={{position: 'absolute', left: 'calc(100% - 53px)', transform: 'translateY(1px)', zIndex: 1}}>
                <div style={{display: 'flex'}}>
                    {canDrop && item.beats === 0 ? <GrooveOneShotIcon /> : <GrooveLoopIcon />}
                    <SubdirectoryArrowLeftIcon style={{widht: '20px', height: '20px', transform: 'rotate(-0.25turn)'}}/>
                </div>
            </Fade>
        </div>
    );
}
